import { useState } from "react";
import { Mail, Clock, X } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { HELP_SECTIONS } from "./helpSections";

interface HelpDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpDrawer({ open, onOpenChange }: HelpDrawerProps) {
  const [activeId, setActiveId] = useState<string>(HELP_SECTIONS[0].id);

  const scrollTo = (id: string) => {
    setActiveId(id);
    const el = document.getElementById(`help-drawer-${id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[720px] p-0 flex flex-col gap-0"
      >
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-border bg-card flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium mb-1">
              Help Center
            </div>
            <h2 className="text-lg font-semibold">How can we help?</h2>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Sticky topic nav */}
          <aside className="w-[200px] shrink-0 border-r border-border bg-muted/20 p-3 overflow-y-auto">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium px-2 mb-2">
              Topics
            </p>
            <nav className="space-y-0.5">
              {HELP_SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = activeId === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-left transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="mt-6 pt-4 border-t border-border/60 space-y-2">
              <a
                href="mailto:support@sorvaine.com"
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">support@sorvaine.com</span>
              </a>
            </div>
          </aside>

          {/* Sections */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-10">
            {HELP_SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <section key={s.id} id={`help-drawer-${s.id}`} className="scroll-mt-4">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                      <Icon className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <h3 className="text-base font-semibold">{s.label}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">{s.intro}</p>

                  <div className="space-y-2">
                    {s.items.map((item, i) => (
                      <details
                        key={i}
                        className="group bg-card rounded-[10px] shadow-sm border border-border/40 overflow-hidden"
                      >
                        <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer list-none hover:bg-muted/30 transition-colors">
                          <span className="text-[13px] font-medium text-foreground">{item.q}</span>
                          <span className="text-muted-foreground text-lg leading-none transition-transform group-open:rotate-45">
                            +
                          </span>
                        </summary>
                        <div className="px-4 pb-4 text-[13px] text-muted-foreground leading-relaxed">
                          {item.a}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              );
            })}

            <section className="bg-card rounded-[10px] shadow-sm border border-border/40 p-6 text-center">
              <h4 className="text-base font-semibold mb-2">Still need help?</h4>
              <a
                href="mailto:support@sorvaine.com"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-[13px] font-semibold transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                support@sorvaine.com
              </a>
            </section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
